export const enUSCommon = {
  locale: {
    label: 'Language',
    ptBR: 'Portugues (BR)',
    enUS: 'English (US)',
  },
  nav: {
    dashboard: 'Dashboard',
    expenses: 'Expenses',
    bills: 'Bills',
    categories: 'Categories',
    accounts: 'Accounts',
    reports: 'Reports',
    investments: 'Investments',
    search: 'Search',
    settings: 'Settings',
    lists: 'Lists',
    agenda: 'Agenda',
    home: 'Home',
    // Product/section name — intentionally identical in both locales.
    backstage: 'Backstage',
    group: {
      finance: 'Finance',
      household: 'Household',
    },
  },
  errorFallback: {
    title: 'Something went wrong',
    message: 'An unexpected error occurred.',
    reload: 'Reload',
  },
  hub: {
    title: 'Home',
    subtitle: 'Choose where to go',
    financeDesc: 'Expenses, bills, reports & investments',
    listsDesc: 'Shopping & to-do lists',
    agendaDesc: 'Events & shared calendar',
    badge: {
      billsDue_one: '{{count}} bill due · {{total}}',
      billsDue_other: '{{count}} bills due · {{total}}',
      billsNone: 'No bills due this week',
      openItems_one: '{{count}} open item',
      openItems_other: '{{count}} open items',
      openItemsNone: 'All caught up',
      nextEvent: 'Next: {{title}}',
      noEvents: 'No events in the next 24h',
    },
  },
  auth: {
    signIn: 'Sign in',
    signingIn: 'Signing in...',
    email: 'Email',
    password: 'Password',
    emailPlaceholder: 'you@example.com',
    passwordPlaceholder: 'Enter your password',
    invalidCredentials: 'Invalid email or password',
    signInToAccount: 'Sign in to your account',
    validationEmail: 'Please enter a valid email address',
    validationPassword: 'Password is required',
    signInWithGoogle: 'Sign in with Google',
    googleNoAccount: 'No account found for this email. Contact your household manager.',
    googleError: 'Google sign-in failed. Please try again.',
    or: 'or',
    signUp: 'Sign up',
    signingUp: 'Creating account...',
    createAccount: 'Create your account',
    alreadyHaveAccount: 'Already have an account?',
    dontHaveAccount: "Don't have an account?",
    name: 'Name',
    namePlaceholder: 'Your name',
    confirmPassword: 'Confirm password',
    confirmPasswordPlaceholder: 'Confirm your password',
    validationName: 'Name is required',
    validationPasswordMin: 'Password must be at least 8 characters',
    validationPasswordMatch: 'Passwords do not match',
    signUpWithGoogle: 'Sign up with Google',
    emailTaken: 'An account with this email already exists',
    signUpSuccess: 'Account created! Please check your email to verify your account.',
    verifyEmailTitle: 'Verify your email',
    verifyEmailMessage: 'We sent a verification link to {{email}}. Please check your inbox.',
    verifyEmailChecking: 'Verifying your email...',
    verifyEmailSuccess: 'Email verified! Redirecting...',
    verifyEmailExpired: 'This verification link has expired.',
    verifyEmailInvalid: 'This verification link is invalid.',
    verifyEmailAlreadyUsed: 'This email has already been verified.',
    resendVerification: 'Resend verification email',
    resendVerificationSent: 'Verification email sent!',
    emailNotVerified: 'Please verify your email before logging in.',
    joinHousehold: 'Join {{household}}',
    invitedBy: 'Invited by {{name}}',
    acceptInvite: 'Accept invite & create account',
    inviteExpired: 'This invite has expired.',
    inviteNotFound: 'This invite link is invalid.',
    inviteUsed: 'This invite has already been used.',
    inviteAccepting: 'Creating your account...',
  },
  actions: {
    edit: 'Edit',
    delete: 'Delete',
    cancel: 'Cancel',
    save: 'Save',
    create: 'Create',
    saving: 'Saving...',
    creating: 'Creating...',
    logout: 'Logout',
  },
  states: {
    loading: 'Loading...',
    error: 'Error',
    empty: 'No items found',
  },
  today: 'Today',
  confirmDelete: 'Delete',
  deleting: 'Deleting...',
  investments: {
    title: 'Investments',
    add: 'Add investment',
    empty: {
      title: 'No investments yet',
      subtitle: 'Start by adding your first investment to track your portfolio',
      cta: 'Add investment',
    },
    form: {
      createTitle: 'New Investment',
      editTitle: 'Edit Investment',
      name: 'Name',
      namePlaceholder: 'E.g.: Treasury Bond 2029',
      type: 'Type',
      typeFixed: 'Fixed Income',
      typeVariable: 'Variable Income',
      institution: 'Institution',
      institutionPlaceholder: 'E.g.: Treasury Direct',
      investedAmount: 'Invested Amount',
      currentValue: 'Current Value',
      purchaseDate: 'Purchase Date',
      maturityDate: 'Maturity Date',
      yieldInfo: 'Yield Info',
      yieldInfoPlaceholder: 'E.g.: 110% CDI',
      save: 'Save',
      saving: 'Saving...',
      create: 'Create Investment',
      update: 'Update',
      delete: 'Delete',
    },
    toast: {
      created: 'Investment created',
      updated: 'Investment updated',
      deleted: 'Investment deleted',
      createError: 'Error creating investment',
      updateError: 'Error updating investment',
      deleteError: 'Error deleting investment',
    },
    confirmDelete: "Are you sure you want to delete '{{name}}'?",
    totalInvested: 'Total Invested',
    totalCurrent: 'Total Current Value',
    gainLoss: 'Gain/Loss',
    detail: {
      edit: 'Edit',
      maturityDate: 'Maturity',
      yieldInfo: 'Yield',
      purchaseDate: 'Purchase Date',
    },
    charts: {
      tabEvolution: 'Evolution',
      tabAllocation: 'Allocation',
      portfolioValue: 'Portfolio value',
      totalInvested: 'Total invested',
      hintMoreSnapshots: 'Add more snapshots to see evolution',
      noData: 'No data',
    },
    snapshots: {
      title: 'Value History',
      record: 'Record Value',
      empty: 'No value records. Record the first value to track evolution.',
      form: {
        createTitle: 'Record Value',
        editTitle: 'Edit Value',
        value: 'Value',
        date: 'Date',
      },
      confirmDelete: 'Delete record from {{date}} ({{value}})?',
      toast: {
        created: 'Value recorded',
        updated: 'Value updated',
        deleted: 'Value deleted',
        error: 'Error saving value',
      },
    },
  },
  previousMonth: 'Previous month',
  nextMonth: 'Next month',
  keyboard: {
    title: 'Keyboard Shortcuts',
    navigation: 'Navigation',
    goToDashboard: 'Go to Dashboard',
    goToExpenses: 'Go to Expenses',
    goToBills: 'Go to Bills',
    goToReports: 'Go to Reports',
    goToSearch: 'Go to Search',
    goToLists: 'Go to Lists',
    goToAgenda: 'Go to Agenda',
    showHelp: 'Show keyboard shortcuts',
    monthNavigation: 'Month Navigation',
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    listNavigation: 'List Navigation',
    moveDown: 'Move down',
    moveUp: 'Move up',
    openDetail: 'Open detail',
    actions: 'Actions',
    togglePaid: 'Toggle paid/unpaid',
    editSelected: 'Edit selected',
    deleteSelected: 'Delete selected',
    general: 'General',
    closeOrDeselect: 'Close modal / Deselect',
  },
  pwa: {
    install: {
      androidMessage: 'Install Nomi on your home screen for quick access.',
      iosMessage: 'Install Nomi on your home screen:',
      iosInstructions: 'Tap <1>Share</1> and then <3>Add to Home Screen</3>',
      notNow: 'Not now',
      install: 'Install',
      dismiss: 'Dismiss',
    },
    update: {
      message: 'New version available',
      notNow: 'Not now',
      update: 'Update',
    },
  },
  offline: {
    banner: "You're offline — showing saved data",
    pendingSync_one: '{{count}} change pending sync',
    pendingSync_other: '{{count}} changes pending sync',
    updatedAgo: 'Updated {{ago}}',
    writeUnavailable: 'Unavailable while offline',
    syncFailed: "Some offline changes couldn't be synced",
  },
} as const;
